import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../state/authStore';

/**
 * Route guard that restricts access by user type.
 *
 * Usage:
 *   <RequireUserType allow="institutional"><ClientsPage /></RequireUserType>
 *   <RequireUserType allow="retail">      <UpgradePage /></RequireUserType>
 *   <RequireUserType allow={['institutional','retail']}>...</RequireUserType>
 *
 * Redirects disallowed users to their home surface.
 */
export default function RequireUserType({ allow, redirect, children }) {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const allowed = Array.isArray(allow) ? allow : [allow];
  const userType = user?.userType || 'retail';
  if (!allowed.includes(userType)) {
    const fallback = redirect || homeForUserType(userType);
    return <Navigate to={fallback} replace />;
  }

  return children;
}

/**
 * Helper: the "home" screen for a given user type, used after login / on role
 * mismatch.  Both surfaces now land on `/public/home` — the Home page is
 * role-aware and renders client rollups / advisor widgets for institutional
 * users and watchlist / portfolio for retail.
 */
export function homeForUserType(_userType) {
  return '/public/home';
}
